import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { X, PaperPlaneRight, CheckCircle, Circle, Plus, ListChecks } from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

function UpdateTaskModal({ task, onClose, onUpdateComplete }) {
  const [note, setNote] = useState('');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState('');
  const [projectClient, setProjectClient] = useState(task.project_client || '');
  const [projects, setProjects] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sending, setSending] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [subtasks, setSubtasks] = useState(task.subtasks || []);
  const noteRef = useRef(null);

  useEffect(() => {
    noteRef.current?.focus();
    axios.get(`${API}/tasks/projects`, { withCredentials: true })
      .then(res => setProjects(res.data)).catch(() => {});
  }, []);

  const filteredSuggestions = projects.filter(
    p => p.toLowerCase().includes(projectClient.toLowerCase()) && p !== projectClient
  );

  const toggleSubtask = async (subtaskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'open' : 'completed';
    try {
      const res = await axios.put(
        `${API}/tasks/${task.task_id}/subtasks/${subtaskId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setSubtasks(res.data.subtasks || []);
    } catch (error) {
      console.error('Failed to toggle subtask:', error);
    }
  };

  const addSubtask = async () => {
    if (!newSubtask.trim()) return;
    setAddingSubtask(true);
    try {
      const res = await axios.post(
        `${API}/tasks/${task.task_id}/subtasks`,
        { title: newSubtask.trim() },
        { withCredentials: true }
      );
      setSubtasks(res.data.subtasks || []);
      setNewSubtask('');
    } catch (error) {
      console.error('Failed to add subtask:', error);
    } finally {
      setAddingSubtask(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;

    setSending(true);
    try {
      const body = { note: note.trim() };
      if (details.trim()) body.details = details.trim();
      if (status) body.status = status;
      if (projectClient.trim() !== (task.project_client || '')) {
        body.project_client = projectClient.trim();
      }

      await axios.post(
        `${API}/tasks/${task.task_id}/quick-update`,
        body,
        { withCredentials: true }
      );

      setNote('');
      setDetails('');
      if (onUpdateComplete) onUpdateComplete();
    } catch (error) {
      console.error('Failed to add update:', error);
      alert('Failed to add update. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-lg w-full"
        data-testid="update-task-modal"
      >
        <div className="border-b border-slate-200 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Update Task</h2>
            <p className="text-xs text-slate-500 truncate max-w-[320px]">{task.title}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-update-modal">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">What did you do? *</label>
            <input
              ref={noteRef}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Drafted contract, reviewed FIRS filing..."
              required
              className="bg-white border border-slate-200 rounded-lg focus:border-slate-900 focus:ring-2 focus:ring-slate-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="note-summary-input"
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 w-full"
                data-testid="update-status-select"
              >
                <option value="">No change</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div className="flex-1 relative">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Project / Client</label>
              <input
                type="text"
                value={projectClient}
                onChange={(e) => { setProjectClient(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="e.g., Access Bank"
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 w-full"
                data-testid="update-project-input"
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md z-10 max-h-32 overflow-y-auto">
                  {filteredSuggestions.map(p => (
                    <button key={p} type="button" className="block w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      onMouseDown={() => { setProjectClient(p); setShowSuggestions(false); }}
                    >{p}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Details (optional)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Additional context, notes, follow-ups..."
              className="bg-white border border-slate-200 rounded-lg focus:border-slate-900 focus:ring-2 focus:ring-slate-100 px-4 py-2 w-full text-sm text-slate-700 min-h-[80px] resize-none"
              data-testid="note-details-input"
            />
          </div>

          {/* Sub-tasks section (only shown if task has sub-tasks or user wants to add) */}
          {(subtasks.length > 0 || newSubtask) && (
            <div className="border border-slate-200 rounded-lg p-3" data-testid="update-subtask-section">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <ListChecks size={13} /> Sub-tasks
                {subtasks.length > 0 && (
                  <span className="font-normal text-slate-400">
                    ({subtasks.filter(s => s.status === 'completed' || s.completed).length}/{subtasks.length})
                  </span>
                )}
              </h4>
              <div className="space-y-1 max-h-[120px] overflow-y-auto">
                {subtasks.map((st) => {
                  const isDone = st.status === 'completed' || st.completed;
                  const stId = st.id || st.title;
                  return (
                    <button
                      key={stId}
                      type="button"
                      onClick={() => stId.startsWith('st_') ? toggleSubtask(stId, st.status || (st.completed ? 'completed' : 'open')) : null}
                      className="flex items-center gap-2 w-full text-left py-1 px-1 rounded hover:bg-slate-50 transition-colors"
                      data-testid={`update-subtask-${stId}`}
                    >
                      {isDone ? (
                        <CheckCircle size={16} weight="fill" className="text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle size={16} className="text-slate-300 flex-shrink-0" />
                      )}
                      <span className={`text-xs ${isDone ? 'line-through text-slate-400' : 'text-slate-700'}`}>{st.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick add sub-task */}
          <div className="flex items-center gap-2">
            <Plus size={14} className="text-slate-300" />
            <input
              type="text"
              value={newSubtask}
              onChange={(e) => setNewSubtask(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubtask(); } }}
              placeholder="Add a sub-task..."
              className="flex-1 text-xs bg-transparent border-none outline-none text-slate-600 placeholder:text-slate-300 py-1"
              data-testid="update-add-subtask-input"
            />
            {newSubtask.trim() && (
              <button type="button" onClick={addSubtask} disabled={addingSubtask} className="text-xs text-navy font-medium" data-testid="update-add-subtask-btn">
                {addingSubtask ? '...' : 'Add'}
              </button>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={sending || !note.trim()}
              className="flex-1 bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-4 py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              data-testid="submit-update-btn"
            >
              <PaperPlaneRight size={14} weight="fill" />
              {sending ? 'Saving...' : 'Save Update'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-2.5 rounded-lg text-sm"
              data-testid="cancel-update-btn"
            >
              Cancel
            </button>
          </div>
        </form>

        <div className="bg-slate-50 border-t border-slate-200 p-4">
          <p className="text-xs text-slate-600">
            Updates are append-only and create an immutable history trail.
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export default UpdateTaskModal;
